import React from 'react';
import classNames from 'classnames';
import { useT } from '../../../translation/src';
import { Tooltip } from '../../../components/Tooltip';

export interface ModeConfig {
  id: string;
  name: string;
  icon?: React.FC;
  disabledTooltip?: string;
}

interface ModesBarProps {
  modes: ModeConfig[];
  currentModeId?: string;
  onModeChange: (modeId: string) => void;
  className?: string;
}

export const ModesBar: React.FC<ModesBarProps> = ({ modes, currentModeId, onModeChange, className }) => {
  if (modes.length <= 1) {
    return null;
  }

  return (
    <div className={classNames('w-full flex items-center justify-center py-2 bg-[rgb(210,210,210)] dark:bg-[rgb(45,45,45)]', className)}>
      <div className="flex items-center justify-center gap-2">
        {modes.map((mode) => (
          <ModeButton key={mode.id} mode={mode} isActive={currentModeId === mode.id} onClick={() => onModeChange(mode.id)} />
        ))}
      </div>
    </div>
  );
};

/**
 * HeaderModesBar - Modes bar for use in the header (no background styling)
 */
interface HeaderModesBarProps {
  modes: ModeConfig[];
  currentModeId?: string;
  onModeChange?: (modeId: string) => void;
  disabledModes?: Set<string>;
  children?: React.ReactNode;
}

export const HeaderModesBar: React.FC<HeaderModesBarProps> = ({ modes, currentModeId, onModeChange, disabledModes, children }) => {
  if (modes.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {modes.map((mode) => (
        <ModeButton key={mode.id} mode={mode} isActive={currentModeId === mode.id} disabled={disabledModes?.has(mode.id)} onClick={() => onModeChange?.(mode.id)}>
          {children}
        </ModeButton>
      ))}
    </div>
  );
};

/**
 * ModeButton - Separate component for mode buttons to avoid hooks in loops
 */
interface ModeButtonProps {
  mode: ModeConfig;
  isActive: boolean;
  disabled?: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}

export const ModeButton: React.FC<ModeButtonProps> = ({ mode, isActive, disabled, onClick, children }) => {
  const ModeIcon = mode.icon;
  const modeTranslatedName = useT(mode.name, { swc: true });
  const disabledTooltipText = useT(mode.disabledTooltip || '', { swc: true });

  const buttonContent = (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={classNames(
        'relative flex items-center gap-2 px-4 py-2 transition-all',
        disabled
          ? 'bg-transparent text-gray-300 dark:text-gray-600 cursor-not-allowed rounded'
          : isActive
            ? 'bg-[rgb(210,210,210)] dark:bg-[rgb(45,45,45)] text-black dark:text-neon-green-300 cursor-pointer rounded-t-lg rounded-b-none'
            : 'bg-transparent text-black dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-700 cursor-pointer rounded',
      )}
      style={
        isActive && !disabled
          ? {
              borderRadius: '12px 12px 0 0',
            }
          : undefined
      }
      aria-label={modeTranslatedName}
      aria-pressed={isActive}
      aria-disabled={disabled}
    >
      {ModeIcon && <ModeIcon className="w-5 h-5" />}
      <span className="text-base font-medium">{modeTranslatedName}</span>
    </button>
  );

  return (
    <div
      className="relative"
      style={{
        transform: 'translateY(25px)',
      }}
    >
      {disabled && mode.disabledTooltip ? (
        <Tooltip message={<span>{disabledTooltipText}</span>} delay={0} placement="top" className="bg-warning-yellow-200 border-warning-yellow-500 text-warning-yellow-700 dark:bg-[#313131] dark:text-warning-yellow-500">
          {buttonContent}
        </Tooltip>
      ) : (
        buttonContent
      )}
      {/* Tools container underneath active tab */}
      {isActive && !disabled && children && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 h-[52px] flex items-center">
          <div className="flex items-center gap-4 scale-[0.85] origin-center">{children}</div>
        </div>
      )}
      {/* Smooth curved edges extending to the sides */}
      {/* Left curved edge */}
      <div
        className="absolute bottom-0 -left-3 w-3 h-3 bg-transparent dark:hidden transition-all duration-150"
        style={{
          boxShadow: isActive && !disabled ? '6px 0 0 0 rgb(210,210,210)' : '6px 0 0 0 transparent',
          borderBottomRightRadius: '12px',
        }}
      />
      <div
        className="absolute bottom-0 -left-3 w-3 h-3 bg-transparent hidden dark:block transition-all duration-150"
        style={{
          boxShadow: isActive && !disabled ? '6px 0 0 0 rgb(45,45,45)' : '6px 0 0 0 transparent',
          borderBottomRightRadius: '12px',
        }}
      />
      {/* Right curved edge */}
      <div
        className="absolute bottom-0 -right-3 w-3 h-3 bg-transparent dark:hidden transition-all duration-150"
        style={{
          boxShadow: isActive && !disabled ? '-6px 0 0 0 rgb(210,210,210)' : '-6px 0 0 0 transparent',
          borderBottomLeftRadius: '12px',
        }}
      />
      <div
        className="absolute bottom-0 -right-3 w-3 h-3 bg-transparent dark:block hidden transition-all duration-150"
        style={{
          boxShadow: isActive && !disabled ? '-6px 0 0 0 rgb(45,45,45)' : '-6px 0 0 0 transparent',
          borderBottomLeftRadius: '12px',
        }}
      />
    </div>
  );
};
